import { useEffect, useRef, useState } from 'react';
import { keyframes } from 'styled-components';

/**
 * Econ-специфичные анимации и хелперы. Общий набор брендовых keyframes живёт
 * в `@shared/theme/animations` (riseIn/popIn/blockDrop/pulse/…) — здесь только
 * то, что нужно разделу Экономист и не имеет смысла в остальном приложении:
 * прорисовка соединительных линий трассировки и пульс критичной аномалии.
 *
 * Никакого framer-motion — чистый CSS + requestAnimationFrame, как во всём
 * проекте. Глобальный `prefers-reduced-motion` (GlobalStyle) уже глушит эти
 * keyframes; `useCountUp` гасит себя сам (см. ниже).
 */

/** Прорисовка SVG-линии слева направо через stroke-dashoffset. На элементе
 *  должны стоять `stroke-dasharray` = длине пути и стартовый `stroke-dashoffset`. */
export const drawLine = keyframes`
  from { stroke-dashoffset: var(--dash-len, 200); }
  to   { stroke-dashoffset: 0; }
`;

/** Узел трассы «вплывает» по горизонтали — для staggered появления цепочки. */
export const flowIn = keyframes`
  from { opacity: 0; transform: translateX(-12px) scale(0.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

/** Тревожный пульс для бейджа критичной аномалии — мягкое свечение, не мигание.
 *  Используется поверх обычного Badge через box-shadow, чтобы привлечь внимание
 *  HITL-ревьюера к не-размеченным critical-отклонениям. */
export const alertPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(183, 28, 28, 0.0); }
  50%      { box-shadow: 0 0 0 4px rgba(183, 28, 28, 0.22); }
`;

/** Линейная прибавка для крупных управленческих чисел (выручка, маржа). */
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Анимированный счётчик: плавно доводит число от 0 (или прошлого значения)
 * до `target` за `duration` мс через requestAnimationFrame.
 *
 * Уважает `prefers-reduced-motion`: при reduce сразу возвращает target без
 * анимации (CSS-глушилка из GlobalStyle на JS-таймеры не действует, поэтому
 * проверяем сами). При смене target анимация перезапускается с текущего
 * отрисованного значения, без скачка.
 */
export function useCountUp(target: number, duration = 650): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || !Number.isFinite(target)) {
      setValue(target);
      fromRef.current = target;
      return;
    }

    const from = fromRef.current;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(progress);
      const current = from + (target - from) * eased;
      setValue(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      // Запомним последнее отрисованное значение как старт следующей анимации.
      fromRef.current = value;
    };
    // value намеренно не в deps: он меняется каждый кадр и перезапустил бы цикл.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return value;
}
